import { ServiceBase } from "../ServiceBase";
import { IWebGLContextManagerService, TResultBase } from "./webgl-context-manager.service.interface";

/**
 * In a web page the browser limits the number of WebGLs contexts.
 * There may be from 8 to 16 contexts, and when that limit is reached, old contexts are lost.
 * In order to prevent that, all functions that create a WebGL context, like "new PIXI.Application" should be decorated with this service
 * and creation will take place only when this service considers there are enough resources.
 */
export class WebGLContextManagerService extends ServiceBase implements IWebGLContextManagerService {
    private _activeContexts = 0;

    public makeCreator<TOptions, TResult extends TResultBase>(creatorFn: (options: TOptions) => Promise<TResult>): (options: TOptions) => Promise<TResult> {
        return async (options: TOptions) => {
            /* The most restrictive browsers (Chrome, Safari and Opera on mobile devices) allow a maximum of 8 webgl context instances
            The limit here is 7 because one webgl context is reserved for the game iframe */
            if (this._activeContexts >= 7) {
                return Promise.reject("Too many WebGL contexts");
            }
            this._activeContexts++;
            try {
                const result: TResult = await creatorFn(options);
                const onContextLost = () => {
                    this._activeContexts--;
                }
                result.canvas.addEventListener("webglcontextlost", onContextLost);
                return {
                    ...result,
                    dispose: () => {
                        result.canvas.removeEventListener("webglcontextlost", onContextLost);
                        /* In PC, pixi will create an WebGL 2 context but on mobile (at least on Chrome) it will create a WebGL 1 context */
                        /* We need to check if the context was lost because we can call dispose on a canvas that already lost it's context
                        and the activeContext count was already decremented. So destroying the canvas and animation objects so should not
                        decrement it again */
                        const webGl2Context = result.canvas.getContext("webgl2");
                        const webGl1Context = result.canvas.getContext("webgl");
                        const isWebGlContextLost = webGl2Context ? webGl2Context.isContextLost() : ( webGl1Context ? webGl1Context.isContextLost() : true);
                        if (!isWebGlContextLost) {
                            this._activeContexts--;
                        }
                        result.dispose()
                    }
                };
            } catch (e) {
                this._activeContexts--;
                throw e;
            }
        };
    }
}
